import { useEffect, useMemo, useState } from "react";
import { SmsHttp, SmsLogRow, SmsPurpose, SmsStatus } from "./types";

export interface SmsLogDay {
  // "YYYY-MM-DD" (yerel saat)
  day: string;
  sent: number;
  failed: number;
}

export interface UseSmsLogOptions {
  http: SmsHttp;
  // Rapor ucu (ör. "/reports/sms-log").
  path: string;
  initialStartDate: string;
  initialEndDate: string;
}

function localIsoDate(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// SMS Gönderim Raporu: filtreler (tarih aralığı, amaç, durum), satırlar, özet
// sayılar ve günlük gönderildi/başarısız dağılımı. Tablo/grafik/sayfalama
// uygulamanın kendi bileşenleriyle çizilir.
export function useSmsLog({ http, path, initialStartDate, initialEndDate }: UseSmsLogOptions) {
  const [rows, setRows] = useState<SmsLogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [purpose, setPurpose] = useState<SmsPurpose | "">("");
  const [status, setStatus] = useState<SmsStatus | "">("");

  useEffect(() => {
    if (!startDate || !endDate) return;
    let cancelled = false;
    setLoading(true);
    http
      .get<SmsLogRow[]>(path, {
        params: { startDate, endDate, purpose: purpose || undefined, status: status || undefined },
      })
      .then((res) => {
        if (!cancelled) setRows(res.data);
      })
      .catch(() => {
        if (!cancelled) setRows([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // http/path sabit kabul edilir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, purpose, status]);

  const sentCount = useMemo(() => rows.filter((r) => r.status === "SENT").length, [rows]);

  const byDay: SmsLogDay[] = useMemo(() => {
    const map = new Map<string, SmsLogDay>();
    for (const row of rows) {
      const day = localIsoDate(new Date(row.createdAt));
      const entry = map.get(day) ?? { day, sent: 0, failed: 0 };
      if (row.status === "SENT") entry.sent += 1;
      else entry.failed += 1;
      map.set(day, entry);
    }
    return Array.from(map.values()).sort((a, b) => a.day.localeCompare(b.day));
  }, [rows]);

  return {
    rows,
    loading,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    purpose,
    setPurpose,
    status,
    setStatus,
    sentCount,
    failedCount: rows.length - sentCount,
    byDay,
  };
}

// Başarısız satırda "#kod açıklama"; OTP paketi yokken normal hatta düşülen
// başarılı gönderimde `fallbackLabel`.
export function smsLogDetail(row: SmsLogRow, fallbackLabel: string): string {
  if (row.status === "FAILED") return [row.responseCode && `#${row.responseCode}`, row.errorMessage].filter(Boolean).join(" ");
  return row.via === "STANDARD_FALLBACK" ? fallbackLabel : "";
}
